/**
 * Phase 3.20a — "Get Certified" hero-button gateway.
 *
 * Markup contract: any element carrying `data-amri-get-certified` AND a
 * `data-href` pointing at `/home/getcertifiedcheck?examType=...&fullCertification=...`
 * is treated as a Get-Certified trigger. Server-rendered by
 * `landingCoursesSection.cshtml`:
 *
 *   <button class="amri-btn"
 *           data-amri-get-certified
 *           data-href="/home/getcertifiedcheck?examType=10&fullCertification=true">
 *     Certification
 *   </button>
 *
 * Click → POST `data-href` → JSON `{ authenticateNeeded?, examInProgress?,
 * redirectUrl? }` (see `GetCertifiedCheckResult.cs`). Branch in this order:
 *
 *   1. `authenticateNeeded === true` → open the existing `#amri-modal-login`
 *      via {@link openModal}. After login the user can click again.
 *   2. `examInProgress === true` → open `#amri-modal-exam-in-progress`,
 *      a 1:1 port of the legacy `ExamInProgressDialog.cshtml` modal. The
 *      "Continue Exam" CTA inside is a placeholder pointing at `/checkout`
 *      until `/education/continuetoexam` is ported in Phase 7+ — see the
 *      `exam-in-progress-cta-destination-divergence` finding.
 *   3. `redirectUrl` populated → just `window.location.href = redirectUrl`.
 *      The matching `/checkout/addXxxtoorder` GET shipped Phase 3.18e mutates
 *      the cart and 302s to `/checkout`.
 *
 * On network failure: silently no-op (the cart-counter widget pattern). A
 * second click retries. The legacy flow had the same shape — failure was
 * invisible to the user.
 *
 * Click delegation is bound to `document` so the handler captures clicks on
 * markup re-rendered after init (the hero carousel rebuilds slides as the
 * user clicks the tabs). Idempotent: `initGetCertifiedHandler()` can be
 * called multiple times safely.
 */

import { openModal } from '../auth/login';

const HOOK = 'data-amri-get-certified';
const HREF_ATTR = 'data-href';

interface GetCertifiedCheckResult {
    redirectUrl?: string;
    authenticateNeeded?: boolean;
    examInProgress?: boolean;
}

async function postGetCertifiedCheck(url: string): Promise<GetCertifiedCheckResult | null> {
    try {
        const response = await fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
            },
        });
        if (!response.ok) {
            return null;
        }
        return (await response.json()) as GetCertifiedCheckResult;
    } catch {
        return null;
    }
}

function onClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
        return;
    }

    const trigger = target.closest<HTMLElement>(`[${HOOK}]`);
    if (trigger === null) {
        return;
    }

    const url = trigger.getAttribute(HREF_ATTR);
    if (url === null || url.length === 0) {
        return;
    }

    event.preventDefault();
    void (async () => {
        const result = await postGetCertifiedCheck(url);
        if (result === null) {
            return;
        }

        if (result.authenticateNeeded === true) {
            openModal('login');
            return;
        }

        if (result.examInProgress === true) {
            openModal('exam-in-progress');
            return;
        }

        if (result.redirectUrl !== undefined && result.redirectUrl.length > 0) {
            window.location.href = result.redirectUrl;
        }
    })();
}

let initialised = false;

export function initGetCertifiedHandler(): void {
    if (initialised) {
        return;
    }
    initialised = true;
    document.addEventListener('click', onClick);
}
